"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.FetchTypes = void 0;
var _zillowAutocompleteFetcher = _interopRequireDefault(require("@zillow/zillow-autocomplete-fetcher"));
var _savedHomesFetcher = _interopRequireDefault(require("@zillow/saved-homes-fetcher"));
var _savedSearchFetcher = _interopRequireDefault(require("@zillow/saved-search-fetcher"));
var _recentSearchFetcher = _interopRequireDefault(require("@zillow/recent-search-fetcher"));
var _arrayIncludes = _interopRequireDefault(require("array-includes"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
// Default location context for autocomplete results. This is a location in Dexter, KS
// located in the middle of the country.
var DEFAULT_CENTROID = {
  lat: 37.17942,
  lon: -96.71591
};
var DEFAULT_FETCH_OPTIONS = {
  centroidOverride: undefined
};

// takes an array and flattens its contents
// returns a single array containing all of the contents of the input array.
var flattenArrays = function flattenArrays(arrayOfArrays) {
  if (arrayOfArrays && Array.isArray(arrayOfArrays)) {
    return arrayOfArrays.reduce(function (acc, cur) {
      return acc.concat(cur);
    }, []);
  }
  return arrayOfArrays;
};
var AutocompleteManager = /*#__PURE__*/function () {
  // constructor
  // argument: config - javascript object with the following properties:
  //  - centroid: A javascript object representing the location context for autocomplete
  //      format: centroid = { lat: [number], lon: [number] }
  //  - regionAddressAutocompleteURL: base URL for Zillow Autocomplete Service V3
  //  - fetchSavedHomes: boolean value if saved/claimed homes should be fetched
  //  - fetchSavedSearches: boolean value if saved searches should be fetched
  //  - recentSearchLocalStorageKey: string value for the key in localstorage where
  //    recent searches should be fetched from
  function AutocompleteManager(config) {
    var _config$resultTypes;
    this.centroid = Object.assign({}, config.centroid ? config.centroid : DEFAULT_CENTROID);
    // SXP-596 Device GPS is generally only accurate up to 4 meters, corresponding to 5 decimal places of c
    this.centroid.lat = this.centroid.lat.toFixed(5);
    this.centroid.lon = this.centroid.lon.toFixed(5);
    this.abKey = config.abKey;
    this.clientId = config.clientId;
    this.resultTypes = (_config$resultTypes = config.resultTypes) !== null && _config$resultTypes !== void 0 ? _config$resultTypes : null;
    this.regionAddressAutocomplete = config.regionAddressAutocompleteURL ? new _zillowAutocompleteFetcher.default(config.regionAddressAutocompleteURL, this.centroid) : null;
    this.savedHomesFetcher = config.fetchSavedHomes ? new _savedHomesFetcher.default() : null;
    this.savedSearchFetcher = config.fetchSavedSearches ? new _savedSearchFetcher.default() : null;
    this.recentSearchFetcher = config.recentSearchLocalStorageKey ? new _recentSearchFetcher.default(config.recentSearchLocalStorageKey) : null;
  }

  // setCentroid
  // sets a new location context for the AutocompleteManager for fetching results
  // argument: latitude [Number] (required)
  // argument: longitude [Number] (required)
  var _proto = AutocompleteManager.prototype;
  _proto.setCentroid = function setCentroid(latitude, longitude) {
    if (typeof latitude !== 'undefined' && typeof longitude !== 'undefined') {
      this.centroid = {
        lat: latitude.toFixed(5),
        lon: longitude.toFixed(5)
      };
    }
  }

  // fetchResults
  // argument: prefix - a String to send to the autocomplete services
  // argument: typesToFetch - an Array of AutcompleteManager.FetchTypes indicating which results to return.
  //  - if typesToFetch is not specified, all autocomplete types that were set up in the config will be returned
  // argument: options - (optional) - options for modifying base fetch behavior.
  // format:
  //      fetchLimit - Number determining max number of results to return.
  //      centroidOverride - an object to pass to the individual autocomplete fetchers to override the centroid from construct time
  // returns a Promise that resolves to an array of autocomplete entries
  ;
  _proto.fetchResults = function fetchResults(prefix, typesToFetch, options) {
    var _this = this;
    var FetchTypes = AutocompleteManager.FetchTypes;
    var mergedOptions = Object.assign(DEFAULT_FETCH_OPTIONS, options);

    // First fetch saved homes, saved searches[, and recent searches
    // Since these typically do not change as the user is typing, we will fetch them once and cache the results
    var shouldFetchSavedHomes = this.savedHomesFetcher && (!typesToFetch || (0, _arrayIncludes.default)(typesToFetch, FetchTypes.SAVED_HOMES));
    var savedHomesPromise = new Promise(function (resolve) {
      if (!shouldFetchSavedHomes) {
        resolve([]);
      } else if (_this.savedHomes) {
        resolve(_this.savedHomes);
      } else {
        _this.savedHomesFetcher.fetchResults().then(function (results) {
          _this.savedHomes = results;
          resolve(results);
        });
      }
    });
    var shouldFetchSavedSearches = this.savedSearchFetcher && (!typesToFetch || (0, _arrayIncludes.default)(typesToFetch, FetchTypes.SAVED_SEARCH));
    var savedSearchesPromise = new Promise(function (resolve) {
      if (!shouldFetchSavedSearches) {
        resolve([]);
      } else if (_this.savedSearches) {
        resolve(_this.savedSearches);
      } else {
        _this.savedSearchFetcher.fetchResults().then(function (results) {
          _this.savedSearches = results;
          resolve(results);
        });
      }
    });
    var shouldFetchRecentSearches = this.recentSearchFetcher && (!typesToFetch || (0, _arrayIncludes.default)(typesToFetch, FetchTypes.RECENT_SEARCH));
    var recentSearchesPromise = shouldFetchRecentSearches ? this.recentSearchFetcher.fetchResults() : Promise.resolve([]);

    // Next, fetch the types that do change based on what the prefix is
    var shouldFetchRegionOrAddress = this.regionAddressAutocomplete && (!typesToFetch || (0, _arrayIncludes.default)(typesToFetch, FetchTypes.REGION) || (0, _arrayIncludes.default)(typesToFetch, FetchTypes.ADDRESS));
    var autocompleteConfigObject = {
      abKey: this.abKey,
      clientId: this.clientId,
      resultCount: mergedOptions.fetchLimit
    };
    if (this.resultTypes) {
      autocompleteConfigObject = Object.assign({}, autocompleteConfigObject, {
        resultTypes: this.resultTypes
      });
    }
    var regionAddressAutocompletePromise = shouldFetchRegionOrAddress ? this.regionAddressAutocomplete.fetchResults(prefix, mergedOptions.centroidOverride, autocompleteConfigObject) : Promise.resolve([]);
    var aggregatedResultPromise = Promise.all([savedHomesPromise, savedSearchesPromise, recentSearchesPromise, regionAddressAutocompletePromise]).then(function (promiseResultArray) {
      return flattenArrays(promiseResultArray).slice(0, mergedOptions.fetchLimit);
    });
    return aggregatedResultPromise;
  };
  return AutocompleteManager;
}();
var FetchTypes = {
  ADDRESS: 'ADDRESS',
  REGION: 'REGION',
  RECENT_SEARCH: 'RECENT_SEARCH',
  SAVED_HOMES: 'SAVED_HOMES',
  SAVED_SEARCH: 'SAVED_SEARCH'
};
exports.FetchTypes = FetchTypes;
AutocompleteManager.FetchTypes = FetchTypes;
var _default = AutocompleteManager;
exports.default = _default;